import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Shield, AlertTriangle, Clock, BarChart3, Wallet, CheckCircle, Settings, Bell, List } from 'lucide-react';

const TradingRulesPage = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    {
      id: 1,
      title: "Introduction",
      icon: BookOpen,
      content: (
        <>
          <p style={{ marginBottom: '12px' }}>
            These Trading Rules govern all accounts operated under Prop Indexa. By purchasing, accessing, or trading any Prop Indexa account, you confirm that you have read, understood, and agreed to comply with all rules listed on this page.
          </p>
          <p>
            These rules are designed to promote responsible trading, fair risk management, and a sustainable trading environment for all participants.
          </p>
        </>
      )
    },
    {
      id: 2,
      title: "Account Activation",
      icon: Clock,
      content: (
        <p>
          Upon successful payment, trading accounts are activated once all blockchain confirmations are met, this may be instantly or up to 30 mins after payment.
        </p>
      )
    },
    {
      id: 3,
      title: "Trading Instruments",
      icon: BarChart3,
      content: (
        <p>
          Traders are permitted to trade only <strong style={{ color: 'var(--accent-gold)' }}>synthetic indices</strong>, trading other instruments would result in hard breach.
        </p>
      )
    },
    {
      id: 4,
      title: "Allowed Trading Methods",
      icon: Settings,
      content: (
        <>
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>4.1 Manual Trading</h4>
          <p style={{ marginBottom: '20px' }}>Manual trading is fully permitted, provided all risk parameters are respected.</p>
          
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>4.2 Bot / EA Trading</h4>
          <ul style={{ marginLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Expert Advisors (EAs), bots, and algorithmic trading systems are allowed.</li>
            <li style={{ marginBottom: '8px' }}>All automated strategies must comply with Prop Indexa's risk and drawdown rules.</li>
            <li style={{ marginBottom: '8px' }}>The use of bots does not exempt traders from any trading limitations.</li>
          </ul>
        </>
      )
    },
    {
      id: 5,
      title: "Risk Management Rules",
      icon: Shield,
      content: (
        <>
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>5.1 Maximum Drawdown</h4>
          <ul style={{ marginLeft: '24px', marginBottom: '20px' }}>
            <li style={{ marginBottom: '8px' }}>Each account has a defined Maximum Drawdown Limit, calculated as a percentage of the initial or peak account balance.</li>
            <li style={{ marginBottom: '8px' }}>Account equity must never fall below the allowed drawdown threshold at any time.</li>
          </ul>
          <div style={{
            background: 'rgba(212, 175, 55, 0.1)',
            border: '1px solid var(--accent-gold)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <p style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              <strong>Example:</strong> If an account is funded with $10,000 and has an 8% maximum drawdown, equity must not fall below $9,200.
            </p>
          </div>
          <p style={{ marginBottom: '24px', color: 'var(--status-error)' }}>
            <strong>⚠️ A breach of maximum drawdown results in immediate account termination.</strong>
          </p>

          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>5.2 Daily Loss Limit</h4>
          <ul style={{ marginLeft: '24px', marginBottom: '12px' }}>
            <li style={{ marginBottom: '8px' }}>A Daily Loss Limit restricts the maximum loss allowed within a single trading day.</li>
            <li style={{ marginBottom: '8px' }}>This limit resets daily at the platform's server time.</li>
          </ul>
          <p style={{ marginBottom: '24px', color: 'var(--status-error)' }}>
            <strong>⚠️ Breaching the daily loss limit may result in account suspension or closure.</strong>
          </p>

          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>5.3 Position Sizing & Risk Exposure</h4>
          <ul style={{ marginLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Traders must respect all position size and risk exposure limits defined by their account.</li>
            <li style={{ marginBottom: '8px' }}>Excessive risk-taking or over-leveraging is strictly prohibited.</li>
            <li style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--error)' }}>Trading without a stop loss is strictly prohibited, as it exposes the trading account to excessive risk. All open positions must have a stop loss set at all times.</li>
          </ul>
        </>
      )
    },
    {
      id: 6,
      title: "Trading Restrictions",
      icon: AlertTriangle,
      content: (
        <>
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>6.1 Market Manipulation</h4>
          <p style={{ marginBottom: '12px' }}>Any form of market manipulation is prohibited, including but not limited to:</p>
          <ul style={{ marginLeft: '24px', marginBottom: '20px' }}>
            <li style={{ marginBottom: '8px' }}>Spoofing or layering</li>
            <li style={{ marginBottom: '8px' }}>Wash trading</li>
            <li style={{ marginBottom: '8px' }}>Abusive order placement</li>
          </ul>

          <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>6.2 Latency & System Abuse</h4>
          <ul style={{ marginLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Exploiting latency, price delays, or platform inefficiencies is forbidden.</li>
            <li style={{ marginBottom: '8px' }}>Using abnormal connections or execution manipulation may result in account termination.</li>
          </ul>
        </>
      )
    },
    {
      id: 7,
      title: "Profit Consistency Rule (INSTANT ACCOUNT ONLY)",
      icon: BarChart3,
      highlight: true,
      content: (
        <>
          <p style={{ marginBottom: '16px' }}>
            At the time of withdrawal, no single trading day may contribute more than <strong style={{ color: 'var(--accent-gold)' }}>25%</strong> of the total accumulated profit.
          </p>
          <p style={{ marginBottom: '20px' }}>
            This rule ensures profits are generated through consistent performance rather than reliance on one unusually large trading day.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h5 style={{ color: 'var(--status-success)', marginBottom: '12px', fontWeight: 600 }}>✅ Example (Approved)</h5>
              <ul style={{ marginLeft: '16px', color: 'var(--text-secondary)' }}>
                <li>Total profit: $4,000</li>
                <li>Highest single-day profit: $900 (22.5%)</li>
              </ul>
            </div>
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h5 style={{ color: 'var(--status-error)', marginBottom: '12px', fontWeight: 600 }}>❌ Example (Not Approved)</h5>
              <ul style={{ marginLeft: '16px', color: 'var(--text-secondary)' }}>
                <li>Total profit: $4,000</li>
                <li>Highest single-day profit: $1,500 (37.5%)</li>
              </ul>
            </div>
          </div>
          
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
            If this rule is breached, traders may continue trading until profits are sufficiently distributed across multiple days.
          </p>
        </>
      )
    },
    {
      id: 8,
      title: "Withdrawals & Payouts",
      icon: Wallet,
      content: (
        <ul style={{ marginLeft: '24px' }}>
          <li style={{ marginBottom: '8px' }}>Withdrawal requests can be submitted once the minimum withdrawal requirements are met.</li>
          <li style={{ marginBottom: '8px' }}>All withdrawal requests are subject to administrative review and approval.</li>
          <li style={{ marginBottom: '8px' }}>Processing times usually takes within 24 - 72hrs.</li>
        </ul>
      )
    },
    {
      id: 9,
      title: "Achievement & Payout Certificates",
      icon: CheckCircle,
      content: (
        <>
          <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
            <li style={{ marginBottom: '8px' }}>Upon account approval or payout approval, an Achievement Certificate may be issued.</li>
            <li style={{ marginBottom: '8px' }}>Certificates are accessible from the Achievements section of the user dashboard.</li>
          </ul>
          <p style={{ marginBottom: '8px' }}>Payout certificates include:</p>
          <ul style={{ marginLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Trader name</li>
            <li style={{ marginBottom: '8px' }}>Payout amount</li>
            <li style={{ marginBottom: '8px' }}>Approval date</li>
          </ul>
        </>
      )
    },
    {
      id: 10,
      title: "Trading Hours & System Availability",
      icon: Clock,
      content: (
        <>
          <p style={{ marginBottom: '16px' }}>
            <strong style={{ color: 'var(--accent-gold)' }}>Synthetic Indices are online 24/7</strong>, you trade at your own pace.
          </p>
          <p style={{ marginBottom: '12px' }}>Prop Indexa is not responsible for losses caused by:</p>
          <ul style={{ marginLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Internet connectivity issues</li>
            <li style={{ marginBottom: '8px' }}>Hardware or software failures</li>
            <li style={{ marginBottom: '8px' }}>Third-party service disruptions</li>
          </ul>
        </>
      )
    },
    {
      id: 11,
      title: "Hidden Rules",
      icon: Shield,
      content: (
        <p>
          There are <strong style={{ color: 'var(--status-success)' }}>no hidden rules</strong> or any other rules aside the above stated. Should there be any changes or adjustments, publications would be made to inform and update traders.
        </p>
      )
    },
    {
      id: 12,
      title: "Rule Violations & Enforcement",
      icon: AlertTriangle,
      content: (
        <>
          <p style={{ marginBottom: '12px' }}>Violations of these rules may result in:</p>
          <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
            <li style={{ marginBottom: '8px' }}>Account suspension</li>
            <li style={{ marginBottom: '8px' }}>Account termination</li>
            <li style={{ marginBottom: '8px' }}>Forfeiture of profits</li>
            <li style={{ marginBottom: '8px' }}>Denial of withdrawals</li>
          </ul>
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Prop Indexa reserves the right to take appropriate action at its sole discretion.
          </p>
        </>
      )
    },
    {
      id: 13,
      title: "Policy Updates",
      icon: Bell,
      content: (
        <ul style={{ marginLeft: '24px' }}>
          <li style={{ marginBottom: '8px' }}>Prop Indexa reserves the right to update or modify these rules at any time.</li>
          <li style={{ marginBottom: '8px' }}>Updated rules become effective immediately upon publication.</li>
          <li style={{ marginBottom: '8px' }}>Continued use of the platform constitutes acceptance of any changes.</li>
        </ul>
      )
    },
    {
      id: 14,
      title: "Acknowledgement",
      icon: CheckCircle,
      content: (
        <p style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
          By trading with Prop Indexa, you acknowledge that you have read, understood, and agreed to all trading rules outlined above.
        </p>
      )
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      padding: '40px 24px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Back Link */}
        <Link to="/" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--accent-gold)',
          textDecoration: 'none',
          marginBottom: '32px',
          fontSize: '14px'
        }} data-testid="back-to-home-link">
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '40px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: 'rgba(212, 175, 55, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <BookOpen size={28} color="var(--accent-gold)" />
          </div>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '4px'
            }} data-testid="trading-rules-title">PROP INDEXA – TRADING RULES</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              Last updated: December 2025
            </p>
          </div>
        </div>

        {/* Table of Contents */}
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px solid var(--border-subtle)',
          padding: '28px 32px',
          marginBottom: '32px'
        }} data-testid="table-of-contents">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '20px'
          }}>
            <List size={20} color="var(--accent-gold)" />
            <h2 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}>Table of Contents</h2>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '8px 24px'
          }}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                data-testid={`toc-item-${section.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  color: section.highlight ? 'var(--accent-gold)' : 'var(--text-secondary)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
                  e.currentTarget.style.color = 'var(--accent-gold)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = section.highlight ? 'var(--accent-gold)' : 'var(--text-secondary)';
                }}
              >
                <span style={{ 
                  fontSize: '13px', 
                  fontWeight: 600,
                  minWidth: '24px'
                }}>{section.id}.</span>
                <span style={{ 
                  fontSize: '14px',
                  fontWeight: section.highlight ? 600 : 400
                }}>{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {sections.map((section) => {
            const IconComponent = section.icon;
            return (
              <div 
                key={section.id}
                id={`section-${section.id}`}
                style={{
                  background: section.highlight ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, var(--bg-secondary) 100%)' : 'var(--bg-secondary)',
                  borderRadius: '16px',
                  border: section.highlight ? '1px solid var(--accent-gold)' : '1px solid var(--border-subtle)',
                  padding: '32px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                data-testid={`rule-section-${section.id}`}
              >
                {section.highlight && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, var(--accent-gold) 0%, var(--accent-gold-hover) 100%)'
                  }} />
                )}
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: section.highlight ? 'rgba(212, 175, 55, 0.2)' : 'rgba(212, 175, 55, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IconComponent size={20} color="var(--accent-gold)" />
                  </div>
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}>
                    {section.id}. {section.title}
                  </h2>
                </div>
                
                <div style={{ 
                  color: 'var(--text-secondary)', 
                  lineHeight: 1.8, 
                  fontSize: '15px',
                  paddingLeft: '52px'
                }}>
                  {section.content}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div style={{
          marginTop: '48px',
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
          border: '1px solid var(--border-primary)',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '24px',
            fontWeight: 600,
            marginBottom: '16px',
            color: 'var(--text-primary)'
          }}>
            Ready to Start Trading?
          </h3>
          <p style={{
            fontSize: '16px',
            color: 'var(--text-secondary)',
            marginBottom: '24px',
            maxWidth: '600px',
            margin: '0 auto 24px'
          }}>
            Now that you understand our trading rules, choose an account that fits your trading style and get started.
          </p>
          <Link 
            to="/#plans" 
            className="btn-primary"
            style={{
              display: 'inline-block',
              padding: '14px 32px',
              textDecoration: 'none'
            }}
            data-testid="view-account-plans-btn"
          >
            View Account Plans
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TradingRulesPage;
